import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, Bed, Wifi, Tv, Coffee, Wind, Bath, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import BookingModal from '../components/BookingModal';
import { images } from '../data/images';

interface Room {
  id: string;
  name: string;
  guests: number;
  beds: string;
  images: string[];
  amenities: string[];
  description: string;
}

export default function RoomDetailPage() {
  const { roomId } = useParams();
  const { t } = useTranslation();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const rooms: Record<string, Room> = {
    double: {
      id: 'double',
      name: t('rooms.types.double'),
      guests: 2,
      beds: t('rooms.roomDetails.double.beds'),
      images: images.rooms.standard.doubleGallery,
      amenities: t('rooms.roomDetails.double.amenities', { returnObjects: true }) as string[],
      description: t('rooms.roomDetails.double.description'),
    },
    triple: {
      id: 'triple',
      name: t('rooms.types.triple'),
      guests: 3,
      beds: t('rooms.roomDetails.triple.beds'),
      images: images.rooms.standard.tripleGallery,
      amenities: t('rooms.roomDetails.triple.amenities', { returnObjects: true }) as string[],
      description: t('rooms.roomDetails.triple.description'),
    },
    quadruple: {
      id: 'quadruple',
      name: t('rooms.types.quadruple'),
      guests: 4,
      beds: t('rooms.roomDetails.quadruple.beds'),
      images: images.rooms.standard.quadGallery,
      amenities: t('rooms.roomDetails.quadruple.amenities', { returnObjects: true }) as string[],
      description: t('rooms.roomDetails.quadruple.description'),
    },
    family: {
      id: 'family',
      name: t('rooms.types.family'),
      guests: 5,
      beds: t('rooms.roomDetails.family.beds'),
      images: images.rooms.standard.familyGallery,
      amenities: t('rooms.roomDetails.family.amenities', { returnObjects: true }) as string[],
      description: t('rooms.roomDetails.family.description'),
    },
  };

  const room = roomId ? rooms[roomId] : null;

  if (!room) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bronze-50 via-brown-50 to-bronze-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-serif text-brown-900 mb-4">{t('rooms.details.roomNotFound')}</h1>
          <Link
            to="/rooms"
            className="inline-flex items-center gap-2 text-bronze-700 hover:text-bronze-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('rooms.details.backToRooms')}
          </Link>
        </div>
      </div>
    );
  }

  const amenityIcons: Record<string, any> = {
    'Free Wi-Fi': Wifi,
    'Air Conditioning': Wind,
    'Flat-screen TV': Tv,
    'Private Bathroom': Bath,
    'Shared Kitchen Access': Coffee,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bronze-50 via-brown-50 to-bronze-100">
      <Navigation />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="relative">
                <div className="aspect-[4/3] relative overflow-hidden group cursor-pointer" onClick={() => setIsGalleryOpen(true)}>
                  <img
                    src={room.images[selectedImageIndex]}
                    alt={`${room.name} - View ${selectedImageIndex + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) => (prev === 0 ? room.images.length - 1 : prev - 1));
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) => (prev === room.images.length - 1 ? 0 : prev + 1));
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                    {selectedImageIndex + 1} / {room.images.length}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 p-4">
                  {room.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === idx
                          ? 'border-bronze-600 ring-2 ring-bronze-300'
                          : 'border-transparent hover:border-bronze-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-8">
                <div className="mb-6">
                  <h1 className="text-4xl font-serif font-bold text-brown-900 mb-2">{room.name}</h1>
                  <p className="text-brown-600 text-lg">{room.description}</p>
                </div>

                <div className="flex items-center gap-6 mb-6 pb-6 border-b border-brown-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-bronze-600" />
                    <span className="text-brown-700">Up to {room.guests} guests</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bed className="w-5 h-5 text-bronze-600" />
                    <span className="text-brown-700">{room.beds}</span>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-brown-900 mb-4">Amenities</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {room.amenities.map((amenity, idx) => {
                      const Icon = amenityIcons[amenity] || Wifi;
                      return (
                        <div key={idx} className="flex items-center gap-3 text-brown-700">
                          <Icon className="w-5 h-5 text-bronze-600 flex-shrink-0" />
                          <span>{amenity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">%</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-green-900 text-lg mb-1">{t('booking.bookDirectTitle')}</h3>
                      <p className="text-green-800 text-sm leading-relaxed">
                        {t('booking.bookDirectDescription')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full bg-gradient-to-r from-bronze-700 to-bronze-800 hover:from-bronze-800 hover:to-bronze-900 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {t('rooms.bookNow')}
                  </button>
                </div>

                <p className="text-sm text-brown-600 text-center">
                  {t('booking.freeCancellation')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isGalleryOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={() => setIsGalleryOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-bronze-400 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="max-w-5xl w-full">
            <img
              src={room.images[selectedImageIndex]}
              alt={`${room.name} - View ${selectedImageIndex + 1}`}
              className="w-full h-auto rounded-lg"
            />

            <div className="flex gap-2 mt-4 justify-center overflow-x-auto pb-4">
              {room.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImageIndex === idx
                      ? 'border-bronze-500 ring-2 ring-bronze-300'
                      : 'border-transparent hover:border-bronze-400'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        selectedDates={null}
      />
    </div>
  );
}
